import { StyleSheet, css } from 'aphrodite'
import { useEffect, useMemo } from 'react'
import { useUser } from '../../contexts/UserContext'
import type { CreateTagData } from '../../types/tag'
import type { User } from '../../types/user'
import { StakeholderType, UserType } from '../../types/user'
import MultiSelect, { type Option } from '../MultiSelect'

interface ContactInfoStepProps {
  data: Partial<CreateTagData>
  onUpdate: (data: Partial<CreateTagData>) => void
  onValidationChange?: (isValid: boolean) => void
  showErrors?: boolean
}

// Combined driver option that can represent either a contact or team member
interface DriverOption {
  id: number
  name: string
  email: string
  isTeamMember: boolean
  userId: number // Always present - either contact.user_id or user.id
}

const ContactInfoStep: React.FC<ContactInfoStepProps> = ({
  data,
  onUpdate,
  onValidationChange,
  showErrors = false,
}) => {
  const { contacts, teamMembers } = useUser()
  const isValid = Boolean(data.assigned_user_id)

  useEffect(() => {
    if (onValidationChange) onValidationChange(isValid)
  }, [isValid, onValidationChange])

  // Compute drivers from cached contacts and team members
  const drivers = useMemo(() => {
    // Get driver contacts and convert to DriverOption
    const driverContacts: DriverOption[] = contacts
      .filter(
        (c) => c.contact_object?.user_type === 'DRIVER' && c.contact_object.id,
      )
      .map((contact) => ({
        id: contact.contact_object.id as number,
        name: contact.contact_object.name || contact.contact_object.email,
        email: contact.contact_object.email,
        isTeamMember: false,
        userId: contact.contact_object.id as number,
      }))

    // Get team members with DRIVER user type and convert to DriverOption
    const driverTeamMembers: DriverOption[] = teamMembers
      .filter((user: User) => user.user_type === 'DRIVER')
      .map((user: User) => ({
        id: user.id,
        name: `${user.first_name} ${user.last_name}`.trim() || user.email,
        email: user.email,
        isTeamMember: true,
        userId: user.id,
      }))

    // Combine and deduplicate by user ID (team members take precedence)
    const allDrivers = [...driverContacts, ...driverTeamMembers]
    return allDrivers.filter(
      (driver, index, self) =>
        index === self.findIndex((d) => d.userId === driver.userId),
    )
  }, [contacts, teamMembers])

  const driverOption = data.assigned_user_id
    ? {
        label:
          drivers.find((d) => d.userId === data.assigned_user_id)?.name || '',
        value:
          drivers
            .find((d) => d.userId === data.assigned_user_id)
            ?.userId.toString() || '',
        name:
          drivers.find((d) => d.userId === data.assigned_user_id)?.name || '',
        email:
          drivers.find((d) => d.userId === data.assigned_user_id)?.email || '',
      }
    : null

  return (
    <div className={css(styles.container)}>
      <h3 className={css(styles.sectionTitle)}>CONTACT INFORMATION</h3>

      <div className={css(styles.formGroup)}>
        <label className={css(styles.label)}>Driver</label>
        <MultiSelect
          isMulti={false}
          placeholder="Select Driver"
          options={useMemo(
            () =>
              drivers.map((driver) => ({
                label: `${driver.name} (${driver.email})${driver.isTeamMember ? ' - Team Member' : ''}`,
                value: driver.userId.toString(),
                name: driver.name,
                email: driver.email,
              })),
            [drivers],
          )}
          value={driverOption}
          showEmailInOption={true}
          onChange={(selected: Option | Option[] | null) => {
            const selectedDriver = drivers.find(
              (d) => d.userId.toString() === (selected as Option | null)?.value,
            )
            if (selectedDriver) {
              onUpdate({
                assigned_user_id: selectedDriver.userId,
                assigned_user: {
                  id: selectedDriver.userId,
                  first_name: selectedDriver.name.split(' ')[0] || '',
                  last_name:
                    selectedDriver.name.split(' ').slice(1).join(' ') || '',
                  email: selectedDriver.email,
                  user_type: UserType.DRIVER,
                  stakeholder_types: [StakeholderType.SUB_HAULER],
                  username: selectedDriver.email,
                  phone_number: '',
                  has_completed_driver_onboarding: false,
                  has_completed_contractor_onboarding: false,
                  current_profile: {
                    id: selectedDriver.userId,
                    stakeholder_type: StakeholderType.SUB_HAULER,
                  },
                },
              })
            } else {
              onUpdate({
                assigned_user_id: undefined,
                assigned_user: undefined,
              })
            }
          }}
        />
        {showErrors && !isValid && (
          <div className={css(styles.errorMessage)}>Please select a driver</div>
        )}
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    padding: '20px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '20px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
  },
  errorMessage: {
    color: '#DC2626',
    fontSize: '12px',
    marginTop: '8px',
    fontWeight: 500,
    textAlign: 'left',
    padding: '8px',
    backgroundColor: '#FEF2F2',
    border: '1px solid #FECACA',
    borderRadius: '6px',
  },
})

export default ContactInfoStep
